import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { useAuthStore } from '@/core/stores/useAuthStore';
import { getProfile, getPlans, type ListPlansResponse } from '@/infrastructure/api/profile';
import { getStores } from '@/infrastructure/api/stores';
import { getStoreInfo } from '@/infrastructure/api/store';
import { getAuditLog } from '@/infrastructure/api/audit';
import { QUERY_KEYS } from '@/infrastructure/query/keys';
import type { StoreDetail } from '@/core/types/store';

export function useProfile() {
  const token = useAuthStore(s => s.token);
  return useQuery({
    queryKey: QUERY_KEYS.PROFILE,
    queryFn: () => getProfile(token!),
    enabled: !!token,
    staleTime: 5 * 60 * 1000,
  });
}

export function useStores(options?: Partial<UseQueryOptions<StoreDetail[], Error>>) {
  const token = useAuthStore(s => s.token);
  return useQuery<StoreDetail[], Error>({
    queryKey: QUERY_KEYS.STORES,
    queryFn: () => getStores(token!),
    enabled: !!token,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

export function usePlans(options?: Partial<UseQueryOptions<ListPlansResponse, Error>>) {
  const token = useAuthStore(s => s.token);
  return useQuery<ListPlansResponse, Error>({
    queryKey: QUERY_KEYS.PLANS,
    queryFn: () => getPlans(token!),
    enabled: !!token,
    staleTime: 10 * 60 * 1000,
    ...options,
  });
}

export function useStoreInfo(storeId: string) {
  const token = useAuthStore(s => s.token);
  return useQuery({
    queryKey: QUERY_KEYS.STORE_INFO(storeId),
    queryFn: () => getStoreInfo(token!, storeId),
    enabled: !!token && !!storeId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useAuditLog(page: number = 1) {
  const token = useAuthStore(s => s.token);
  return useQuery({
    queryKey: QUERY_KEYS.AUDIT_LOG(page),
    queryFn: () => getAuditLog(token!, page, 20),
    enabled: !!token,
    staleTime: 1 * 60 * 1000,
  });
}
